import { useEffect, useState } from "react";
import { Facebook, CheckCircle2, Loader2, HelpCircle, ChevronDown, History, ExternalLink } from "lucide-react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]";

const SOURCE_LABELS = {
  tournament: "Tournament",
  promo: "Promo",
  merch: "Merchandise",
  partner: "Partner",
  announcement: "League news",
  champion: "Champion",
  "live-stream": "Live stream poster",
  wanted: "WANTED poster",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

export function FacebookConnect() {
  const [status, setStatus] = useState(null); // {configured, page_name, valid?}
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);
  const [posts, setPosts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/facebook/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false });
    }
  };

  useEffect(() => {
    api.get("/admin/facebook/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
    api.get("/admin/facebook/posts").then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, []);

  const connect = async () => {
    if (!pageId.trim() || !token.trim()) {
      toast.error("Enter both your Page ID and access token.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/facebook/config", { page_id: pageId.trim(), page_access_token: token.trim() });
      toast.success(`Connected to ${data.page_name || "your Page"}!`);
      setToken("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not connect to Facebook");
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.configured && status?.valid !== false;

  return (
    <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4" data-testid="facebook-connect">
      <div className="flex items-center gap-2">
        <Facebook className="h-5 w-5" style={{ color: "#1877F2" }} />
        <h3 className="font-heading font-bold text-lg">Facebook Connection</h3>
      </div>
      <p className="text-xs text-[#555555]">
        Connect your league Facebook Page once. Then a <strong>Share to Facebook</strong> button appears on tournaments, promos, merchandise, partners, champions and posters — letting you post the image with your own caption in one click.
      </p>

      {connected ? (
        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 inline-flex items-center gap-2" data-testid="fb-connected-badge">
          <CheckCircle2 className="h-4 w-4" /> Connected to <strong>{status.page_name}</strong>
        </div>
      ) : status?.configured && status?.valid === false ? (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">Saved token is no longer valid: {status.error}. Re-connect below.</div>
      ) : status === null ? (
        <p className="text-sm text-[#555555] inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</p>
      ) : (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">Not connected yet. Add your Page ID and access token below.</div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Facebook Page ID</label>
          <input className={input} value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="e.g. 100093645651638" data-testid="fb-page-id-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1">Page Access Token {connected && <span className="text-xs text-[#888888]">(leave blank to keep current)</span>}</label>
          <input className={input} type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste your long-lived Page token" data-testid="fb-token-input" />
        </div>
      </div>

      <button onClick={connect} disabled={busy} data-testid="fb-connect-btn" className="inline-flex items-center gap-2 text-white rounded-md px-5 py-2.5 font-semibold disabled:opacity-60" style={{ backgroundColor: "#1877F2" }}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</> : <><Facebook className="h-4 w-4" /> {connected ? "Update connection" : "Connect & test"}</>}
      </button>

      <div className="border-t border-[#E5E7EB] pt-3">
        <button onClick={() => setHelp((h) => !h)} className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold" data-testid="fb-help-toggle">
          <HelpCircle className="h-4 w-4" /> How do I get my Page ID and access token? <ChevronDown className={`h-4 w-4 transition-transform ${help ? "rotate-180" : ""}`} />
        </button>
        {help && (
          <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-[#444444]">
            <li>Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[#1E63C4] underline">developers.facebook.com/apps</a> and create an app (choose <strong>Other → Business</strong>). Keep it in <strong>Development</strong> mode — no review needed.</li>
            <li>Open the <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noopener noreferrer" className="text-[#1E63C4] underline">Graph API Explorer</a>. Select your app, click <strong>Generate Access Token</strong>, and grant <code>pages_manage_posts</code>, <code>pages_read_engagement</code> and <code>pages_show_list</code>. Pick your league Page.</li>
            <li>In the Explorer, run <code>me/accounts</code>. In the results find your Page — copy its <strong>id</strong> (that&apos;s your Page ID) and its <strong>access_token</strong>.</li>
            <li>That token is long-lived for your own Page. Paste both values above and click <strong>Connect &amp; test</strong>.</li>
            <li>Tip: verify the token never expires at <a href="https://developers.facebook.com/tools/debug/accesstoken" target="_blank" rel="noopener noreferrer" className="text-[#1E63C4] underline">Access Token Debugger</a> (Expires: Never).</li>
          </ol>
        )}
      </div>

      {posts.length > 0 && (
        <div className="border-t border-[#E5E7EB] pt-3">
          <h4 className="text-sm font-semibold text-[#333333] mb-2 inline-flex items-center gap-1.5">
            <History className="h-4 w-4 text-[#1E63C4]" /> Recently shared to Facebook
          </h4>
          <ul className="space-y-2" data-testid="fb-history">
            {posts.map((p) => (
              <li key={p.id} className="flex items-start gap-3 text-sm border border-[#E5E7EB] rounded-md p-2.5" data-testid={`fb-history-${p.id}`}>
                {p.image && <img src={fileUrl(p.image)} alt="" className="h-10 w-10 object-cover rounded shrink-0 bg-[#0B0B0C]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-[#333333] line-clamp-2">{p.caption || "(no caption)"}</p>
                  <p className="text-xs text-[#888888] mt-0.5">{SOURCE_LABELS[p.source] || "Post"} · {fmtDate(p.created_at)}</p>
                </div>
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="shrink-0 mt-0.5" style={{ color: "#1877F2" }} data-testid={`fb-history-view-${p.id}`} aria-label="View on Facebook">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
